import { useCallback } from 'react'

import { changedMeetingFilters, useIsMyCalendlyScope } from 'components/meetings/filter_modal/utils'
import { mapChangedFiltersForAnalytics } from 'features/meetings/utils'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { isMeetingsTabPathPrefix } from 'hooks/home/tabs'
import { useFilters } from 'hooks/meetings/filters'
import { isCalendlyMeeting } from 'shared/utils'

import { useMeetingsContext } from '../contexts/MeetingsContext'
import type { Meeting } from '../types'

const LOCATION_CONTACT = 'interactions_feed_all'
const LOCATION_MEETINGS = 'meetings_page'

const ACTION_VIEW_DETAILS = 'view_meeting_details'
const ACTION_JOIN = 'join'
const ACTION_CLICK_RESULT = 'click_result'

/**
 * Returns analytics tracking callbacks for a meeting card.
 *
 * Fires `MEETING_INTERACTION` events for viewing meeting details and joining
 * a meeting. When the consumer is rendered within the contact page feed,
 * the tracking location is set to `interactions_feed_all`; otherwise it
 * defaults to `meetings_page`. An additional `SEARCH_INTERACTION` event is
 * fired on view-details when an active search keyword filter is present.
 *
 * @param uuid - UUID of the meeting being tracked
 * @param source - Calendar source of the meeting, used to determine if it is a Calendly meeting
 * @param meetingIntelligence - Meeting Intelligence data; its UUID is included in tracking payloads
 */
export const useMeetingTracking = ({ uuid, source, meetingIntelligence }: Pick<Meeting, 'uuid' | 'source' | 'meetingIntelligence'>) => {
  const track = useTrack()
  const { trackFromContactPage } = useMeetingsContext() ?? { trackFromContactPage: false }
  const { getFilterParams } = useFilters()
  const isMyCalendlyScope = useIsMyCalendlyScope()

  const location = trackFromContactPage ? LOCATION_CONTACT : LOCATION_MEETINGS
  const miEventUuid = trackFromContactPage ? null : meetingIntelligence?.uuid

  const trackViewDetails = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: ACTION_VIEW_DETAILS,
        location,
        event_uuid: uuid,
        mi_event_uuid: miEventUuid,
        is_calendly_meeting: isCalendlyMeeting({ source }),
      },
    })

    const filterParams = getFilterParams()
    if (filterParams.search_keyword) {
      const isMeetingsPage = typeof window !== 'undefined' && isMeetingsTabPathPrefix(window.location.pathname)
      const changedFilters = changedMeetingFilters(isMeetingsPage, isMyCalendlyScope, filterParams)
      track({
        name: TRACKING_EVENT_NAMES.SEARCH_INTERACTION,
        properties: {
          action: ACTION_CLICK_RESULT,
          search_text: filterParams.search_keyword,
          search_type: 'text',
          location: LOCATION_MEETINGS,
          filters: mapChangedFiltersForAnalytics(changedFilters),
        },
      })
    }
  }, [track, uuid, source, miEventUuid, location, getFilterParams, isMyCalendlyScope])

  const trackJoin = useCallback(() => {
    track({
      name: TRACKING_EVENT_NAMES.MEETING_INTERACTION,
      properties: {
        action: ACTION_JOIN,
        location,
        event_uuid: uuid,
        mi_event_uuid: miEventUuid,
        is_calendly_meeting: isCalendlyMeeting({ source }),
      },
    })
  }, [track, uuid, source, miEventUuid, location])

  return { trackViewDetails, trackJoin }
}
